/**
 * Error Boundary Component
 *
 * This component catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * Integrated with Sentry for error tracking and monitoring.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Button } from 'reactstrap';
import { logError } from '../../utils/errorHandling';
import config from '../../configs/env';
import { captureSentryException } from '../../configs/sentry';

class ErrorBoundary extends React.Component {
 constructor(props) {
 super(props);
 this.state = { 
 hasError: false, 
 error: null, 
 errorInfo: null,
 errorCount: 0
 };
 }

 static getDerivedStateFromError(error) {
 // Update state so the next render will show the fallback UI
 return { hasError: true };
 }

 componentDidCatch(error, errorInfo) {
 // Update state with error details
 this.setState(prevState => ({
 error,
 errorInfo,
 errorCount: prevState.errorCount + 1
 }));

 // Log the error
 logError(
 `ErrorBoundary: ${this.props.name || 'UnnamedComponent'}`,
 error,
 {
 componentStack: errorInfo.componentStack,
 props: this.props.logProps ? this.sanitizeProps(this.props) : undefined
 }
 );

 // Send error to Sentry
 captureSentryException(error, {
 componentName: this.props.name || 'UnnamedComponent',
 componentStack: errorInfo.componentStack,
 errorCount: this.state.errorCount + 1,
 props: this.props.logProps ? this.sanitizeProps(this.props) : undefined,
 });

 // Call onError callback if provided
 if (this.props.onError) {
 this.props.onError(error, errorInfo);
 }
 }
 
 // Remove sensitive or circular data from props before logging
 sanitizeProps(props) {
 const { children, ...rest } = props;
 return rest;
 }

 resetError = () => {
 this.setState({ 
 hasError: false, 
 error: null, 
 errorInfo: null 
 });
 
 // Call onReset callback if provided
 if (this.props.onReset) {
 this.props.onReset();
 }
 }

 render() {
 const { hasError, error, errorInfo, errorCount } = this.state;
 const { 
 fallback,
 maxRetries = 3,
 title = 'Something went wrong',
 message = 'We\'re sorry, but there was an error loading this component.',
 buttonText = 'Try Again',
 showDetails = !config.isProduction
 } = this.props;
 
 if (hasError) {
 // If max retries exceeded and we have a fallback, render it
 if (errorCount > maxRetries && fallback) {
 return fallback;
 }
 
 // If custom fallback UI is provided as a function, use it
 if (typeof this.props.fallbackRender === 'function') {
 return this.props.fallbackRender({
 error,
 errorInfo,
 resetError: this.resetError
 });
 }
 
 // Default fallback UI
 return (
 <Card className="error-boundary-card">
 <CardBody>
 <h3>{title}</h3>
 <p>{message}</p>
 <Button color="primary" onClick={this.resetError}>
 {buttonText}
 </Button>
 {showDetails && error && (
 <details className="mt-2">
 <summary>Error Details</summary>
 <pre className="error-stack">{error.toString()}</pre>
 {errorInfo && (
 <pre className="component-stack">{errorInfo.componentStack}</pre>
 )}
 </details>
 )}
 </CardBody>
 </Card>
 );
 }

 // When there's no error, render children normally
 return this.props.children;
 }
}

ErrorBoundary.propTypes = {
 /** Component name for error reporting */
 name: PropTypes.string,
 
 /** Child components to render */
 children: PropTypes.node.isRequired,
 
 /** Custom title for error message */
 title: PropTypes.string,
 
 /** Custom error message */
 message: PropTypes.string,
 
 /** Custom button text */
 buttonText: PropTypes.string,
 
 /** Whether to show error details */
 showDetails: PropTypes.bool,
 
 /** Maximum number of retries before showing fallback */
 maxRetries: PropTypes.number,
 
 /** Fallback component to render when max retries exceeded */
 fallback: PropTypes.node,
 
 /** Function that returns fallback UI */
 fallbackRender: PropTypes.func,
 
 /** Callback when error occurs */
 onError: PropTypes.func,
 
 /** Callback when error is reset */
 onReset: PropTypes.func,
 
 /** Whether to log props in error report */
 logProps: PropTypes.bool
};

ErrorBoundary.defaultProps = {
 name: 'UnnamedComponent',
 showDetails: !config.isProduction,
 maxRetries: 3,
 logProps: false
};

export default ErrorBoundary;